/**
 * usePrintQueue — React Query hooks for the server-side print queue.
 *
 * Two hooks:
 *   usePrintQueueCount()  — badge count only (key: ['print-queue-count'])
 *   usePrintQueue()       — list + add/setQuantity/remove/clear mutations
 *                           (keys: ['print-queue'] + ['print-queue-count'])
 *
 * Both subscribe to the 'print-queue:refresh' CustomEvent dispatched by
 * WebSocketContext when a colleague triggers a print_queue WS notification,
 * and invalidate their query keys so counts / lists stay live.
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHttp } from '../dry/hooks';

export interface PrintQueueItem {
  id: string;
  qr_code_id: string;
  quantity: number;
  code: string;
  created_at?: string;
  label_title?: string;
  target_kind?: string;
  batch_id?: string;
}

// ── count ─────────────────────────────────────────────────────────────────────

/**
 * Fetches the print queue item count for the badge.
 *
 * Returns the full React Query result object; consumers destructure:
 *   const { data: count = 0 } = usePrintQueueCount();
 *
 * Defensively unwraps both flat `{ count }` and nested `{ data: { count } }`
 * API response envelopes.
 *
 * The count endpoint is manager+ only. Pass `{ enabled: false }` (e.g. for a
 * non-manager session) to skip the request entirely and avoid a 403.
 */
export function usePrintQueueCount(options?: { enabled?: boolean }) {
  const http = useHttp();
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  // Subscribe to the forwarded WS notification → invalidate so the badge
  // stays in sync without a full page reload.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['print-queue-count'] });
    };
    window.addEventListener('print-queue:refresh', handler);
    return () => window.removeEventListener('print-queue:refresh', handler);
  }, [queryClient]);

  return useQuery<number>({
    queryKey: ['print-queue-count'],
    enabled,
    queryFn: async () => {
      const res = await http.get('/qr-codes/print-queue/count');
      const body = (res as any).data as any;
      return body?.count ?? body?.data?.count ?? 0;
    },
    refetchOnWindowFocus: true,
  });
}

// ── list + mutations ──────────────────────────────────────────────────────────

/**
 * Fetches the full print queue list and exposes mutations.
 *
 * add({ code_id, quantity? })   → POST /qr-codes/print-queue
 * setQuantity({ id, quantity }) → PATCH /qr-codes/print-queue/{id}
 * remove(id)                    → DELETE /qr-codes/print-queue/{id}
 * clear()                       → POST /qr-codes/print-queue/clear
 *
 * All mutations invalidate both ['print-queue'] and ['print-queue-count'].
 * The 'print-queue:refresh' window event also invalidates both keys.
 */
export function usePrintQueue() {
  const http = useHttp();
  const queryClient = useQueryClient();

  const invalidateBoth = () => {
    queryClient.invalidateQueries({ queryKey: ['print-queue'] });
    queryClient.invalidateQueries({ queryKey: ['print-queue-count'] });
  };

  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['print-queue'] });
      queryClient.invalidateQueries({ queryKey: ['print-queue-count'] });
    };
    window.addEventListener('print-queue:refresh', handler);
    return () => window.removeEventListener('print-queue:refresh', handler);
  }, [queryClient]);

  const listQuery = useQuery<PrintQueueItem[]>({
    queryKey: ['print-queue'],
    queryFn: async () => {
      const res = await http.get('/qr-codes/print-queue');
      const body = (res as any).data as any;
      return (Array.isArray(body) ? body : (body?.data ?? [])) as PrintQueueItem[];
    },
    refetchOnWindowFocus: true,
  });

  const add = useMutation({
    mutationFn: async (payload: { code_id?: string; quantity?: number; [key: string]: any }) => {
      await http.post('/qr-codes/print-queue', payload);
    },
    onSuccess: invalidateBoth,
  });

  const setQuantity = useMutation({
    mutationFn: async ({ id, quantity }: { id: string; quantity: number }) => {
      await http.patch<any>(`/qr-codes/print-queue/${id}`, { quantity });
    },
    onSuccess: invalidateBoth,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await http.delete<any>(`/qr-codes/print-queue/${id}`);
    },
    onSuccess: invalidateBoth,
  });

  const clear = useMutation({
    mutationFn: async () => {
      await http.post('/qr-codes/print-queue/clear', {});
    },
    onSuccess: invalidateBoth,
  });

  return {
    ...listQuery,
    items: listQuery.data ?? [],
    add: add.mutate,
    setQuantity: setQuantity.mutate,
    remove: remove.mutate,
    clear: clear.mutate,
  };
}
